import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';

const DEFAULT_SITE_NAME = 'Strashin Developments';
const DEFAULT_DESCRIPTION =
  'Strashin Developments delivers sustainable commercial real estate with smart building systems and tenant-first amenities.';
const DEFAULT_IMAGE = '/logo.jpg';
const DEFAULT_CANONICAL_ORIGIN = 'https://strashindevelopments.com';

type OpenGraphOptions = {
  type?: 'website' | 'article';
  image?: string;
};

export interface SeoProps {
  title?: string;
  description?: string;
  canonicalPath?: string | null;
  noIndex?: boolean;
  openGraph?: OpenGraphOptions;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>> | null;
}

const ensureMetaTag = (
  selector: string,
  attribute: 'name' | 'property',
  value: string,
): HTMLMetaElement => {
  const lookup = `${selector}[${attribute}="${value}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(lookup);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  return element;
};

const ensureLinkTag = (rel: string): HTMLLinkElement => {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  return element;
};

const tryResolveOrigin = (value: string): string | null => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    return parsed.origin;
  } catch {
    return null;
  }
};

const resolveOrigin = (canonicalOriginOverride: string): string => {
  const overriddenOrigin = tryResolveOrigin(canonicalOriginOverride);
  if (overriddenOrigin) {
    return overriddenOrigin;
  }

  if (typeof window === 'undefined') {
    return DEFAULT_CANONICAL_ORIGIN;
  }

  try {
    const originValue = window.location.origin;
    return typeof originValue === 'string' && originValue.length > 0
      ? originValue
      : DEFAULT_CANONICAL_ORIGIN;
  } catch {
    return DEFAULT_CANONICAL_ORIGIN;
  }
};

export function Seo({
  title,
  description,
  canonicalPath,
  noIndex = false,
  openGraph,
  structuredData = null,
}: SeoProps): null {
  const location = useLocation();
  const siteSettingsQuery = useSiteSettingsDetail();

  const siteName = (siteSettingsQuery.data?.brand?.siteName ?? '').trim();
  const resolvedSiteName = siteName.length > 0 ? siteName : DEFAULT_SITE_NAME;
  const defaultDescription = (siteSettingsQuery.data?.seo?.defaultDescription ?? '').trim();
  const resolvedDefaultDescription =
    defaultDescription.length > 0 ? defaultDescription : DEFAULT_DESCRIPTION;
  const canonicalOriginOverride = (siteSettingsQuery.data?.seo?.canonicalOrigin ?? '').trim();
  const resolvedCanonicalOriginOverride =
    canonicalOriginOverride.length > 0 ? canonicalOriginOverride : DEFAULT_CANONICAL_ORIGIN;
  const defaultOgImageUrl = (siteSettingsQuery.data?.brand?.defaultOgImageUrl ?? '').trim();
  const resolvedDefaultImage = defaultOgImageUrl.length > 0 ? defaultOgImageUrl : DEFAULT_IMAGE;
  const resolvedDescription = (description ?? '').trim();
  const resolvedMetaDescription =
    resolvedDescription.length > 0 ? resolvedDescription : resolvedDefaultDescription;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const { document } = window;

    const origin = resolveOrigin(resolvedCanonicalOriginOverride);
    const fallbackPath = `${location.pathname}${location.search}`;
    let canonicalTarget = fallbackPath;
    if (canonicalPath != null) {
      canonicalTarget = canonicalPath;
    }
    const canonicalUrl = new URL(canonicalTarget, origin).href;
    const trimmedTitle = title?.trim() ?? '';
    const pageTitle =
      trimmedTitle.length > 0 ? `${trimmedTitle} · ${resolvedSiteName}` : resolvedSiteName;
    const ogImage = openGraph?.image ?? resolvedDefaultImage;
    const ogType = openGraph?.type ?? 'website';
    const shouldApplyNoIndex = typeof noIndex === 'boolean' ? noIndex : false;

    const previousTitle = document.title;
    document.title = pageTitle;

    const canonicalLink = ensureLinkTag('canonical');
    canonicalLink.setAttribute('href', canonicalUrl);

    const descriptionTag = ensureMetaTag('meta', 'name', 'description');
    descriptionTag.setAttribute('content', resolvedMetaDescription);

    const ogTitle = ensureMetaTag('meta', 'property', 'og:title');
    ogTitle.setAttribute('content', pageTitle);

    const ogDesc = ensureMetaTag('meta', 'property', 'og:description');
    ogDesc.setAttribute('content', resolvedMetaDescription);

    const ogUrl = ensureMetaTag('meta', 'property', 'og:url');
    ogUrl.setAttribute('content', canonicalUrl);

    const ogTypeTag = ensureMetaTag('meta', 'property', 'og:type');
    ogTypeTag.setAttribute('content', ogType);

    const ogImageTag = ensureMetaTag('meta', 'property', 'og:image');
    ogImageTag.setAttribute('content', ogImage);

    const twitterCard = ensureMetaTag('meta', 'name', 'twitter:card');
    twitterCard.setAttribute('content', 'summary_large_image');

    const twitterTitle = ensureMetaTag('meta', 'name', 'twitter:title');
    twitterTitle.setAttribute('content', pageTitle);

    const twitterDesc = ensureMetaTag('meta', 'name', 'twitter:description');
    twitterDesc.setAttribute('content', resolvedMetaDescription);

    const twitterImage = ensureMetaTag('meta', 'name', 'twitter:image');
    twitterImage.setAttribute('content', ogImage);

    let robotsTag: HTMLMetaElement | null = null;
    if (shouldApplyNoIndex) {
      robotsTag = ensureMetaTag('meta', 'name', 'robots');
      robotsTag.setAttribute('content', 'noindex, nofollow');
    }

    const structuredEntries = Array.isArray(structuredData)
      ? structuredData
      : structuredData
        ? [structuredData]
        : [];
    const structuredScripts = structuredEntries.map((entry, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-structured', 'true');
      script.setAttribute('data-seo-index', String(index));
      script.textContent = JSON.stringify(entry);
      document.head.appendChild(script);
      return script;
    });

    return () => {
      document.title = previousTitle;
      if (shouldApplyNoIndex && robotsTag) {
        robotsTag.parentElement?.removeChild(robotsTag);
      }
      structuredScripts.forEach((script) => {
        script.parentElement?.removeChild(script);
      });
    };
  }, [
    canonicalPath,
    resolvedCanonicalOriginOverride,
    resolvedDefaultImage,
    resolvedMetaDescription,
    resolvedSiteName,
    location.pathname,
    location.search,
    noIndex,
    openGraph?.image,
    openGraph?.type,
    title,
    structuredData,
  ]);

  return null;
}
